import { DEFAULT_QUERY } from '../documents.constants'
import { DocumentsFilters, DocumentsQuery, DocumentsSortField } from '../documents.types'

// Default-valued keys are stripped from the URL rather than always written, so an unfiltered
// view stays a clean /app/documents rather than a wall of query params.
const SORT_FIELDS: readonly DocumentsSortField[] = [
  'title',
  'category',
  'created_at',
  'published_at',
  'size_bytes',
]

function isSortField(value: string): value is DocumentsSortField {
  return (SORT_FIELDS as readonly string[]).includes(value)
}

// 'tags' is deliberately excluded — it's an array, not a scalar, so it needs its own
// comma-joined read/write below rather than this generic loop's `String(value) === default` check.
const FILTER_KEYS = [
  'search',
  'category',
  'organisation',
  'source',
  'visibility',
  'fileSizeBand',
  'uploadedFrom',
  'uploadedTo',
  'publishedFrom',
  'publishedTo',
] as const satisfies readonly (keyof DocumentsFilters)[]

export function parseDocumentsQuery(params: URLSearchParams): DocumentsQuery {
  const page = Number(params.get('page'))
  const pageSize = Number(params.get('pageSize'))
  const sortField = params.get('sortField')
  const tagsParam = params.get('tags')

  return {
    search: params.get('search') ?? DEFAULT_QUERY.search,
    category: params.get('category') ?? DEFAULT_QUERY.category,
    tags: tagsParam ? tagsParam.split(',').filter(Boolean) : DEFAULT_QUERY.tags,
    organisation: params.get('organisation') ?? DEFAULT_QUERY.organisation,
    source: params.get('source') ?? DEFAULT_QUERY.source,
    visibility:
      (params.get('visibility') as DocumentsFilters['visibility'] | null) ??
      DEFAULT_QUERY.visibility,
    georeferencedOnly: params.get('georeferencedOnly') === 'true',
    fileSizeBand:
      (params.get('fileSizeBand') as DocumentsFilters['fileSizeBand'] | null) ??
      DEFAULT_QUERY.fileSizeBand,
    uploadedFrom: params.get('uploadedFrom'),
    uploadedTo: params.get('uploadedTo'),
    publishedFrom: params.get('publishedFrom'),
    publishedTo: params.get('publishedTo'),
    page: Number.isInteger(page) && page > 0 ? page : DEFAULT_QUERY.page,
    pageSize: Number.isInteger(pageSize) && pageSize > 0 ? pageSize : DEFAULT_QUERY.pageSize,
    sort: {
      field: sortField && isSortField(sortField) ? sortField : DEFAULT_QUERY.sort.field,
      direction: params.get('sortDirection') === 'asc' ? 'asc' : DEFAULT_QUERY.sort.direction,
    },
  }
}

/**
 * Returns a NEW URLSearchParams with the patch applied — never mutates the params passed in.
 */
export function applyDocumentsQueryPatch(
  params: URLSearchParams,
  patch: Partial<DocumentsQuery>,
): URLSearchParams {
  const next = new URLSearchParams(params)

  for (const key of FILTER_KEYS) {
    if (!(key in patch)) continue
    const value = patch[key]
    if (value == null || value === '' || value === DEFAULT_QUERY[key]) {
      next.delete(key)
    } else {
      next.set(key, String(value))
    }
  }

  if ('georeferencedOnly' in patch) {
    if (patch.georeferencedOnly) next.set('georeferencedOnly', 'true')
    else next.delete('georeferencedOnly')
  }

  if ('tags' in patch) {
    if (patch.tags && patch.tags.length > 0) next.set('tags', patch.tags.join(','))
    else next.delete('tags')
  }

  if ('page' in patch) {
    if (patch.page && patch.page !== DEFAULT_QUERY.page) next.set('page', String(patch.page))
    else next.delete('page')
  }

  if ('pageSize' in patch) {
    if (patch.pageSize && patch.pageSize !== DEFAULT_QUERY.pageSize) {
      next.set('pageSize', String(patch.pageSize))
    } else {
      next.delete('pageSize')
    }
  }

  if (patch.sort) {
    if (patch.sort.field !== DEFAULT_QUERY.sort.field) next.set('sortField', patch.sort.field)
    else next.delete('sortField')

    if (patch.sort.direction !== DEFAULT_QUERY.sort.direction) {
      next.set('sortDirection', patch.sort.direction)
    } else {
      next.delete('sortDirection')
    }
  }

  return next
}
